/**
 * Phase K — FAQ JSON-LD helpers.
 *
 * Small hand-rolled JSON-LD blocks for FAQ sections on Mishap Corrector,
 * Transfer Audit, and About pages. Answers are real product statements — no
 * filler, no invented facts.
 */
export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export const MISHAP_FAQ = [
  {
    q: "What is the Mishap Corrector?",
    a: "A free tool that reads your Valencia (or other Florida community college) transcript and compares it against the actual prerequisite tracking sheet for your target program. It flags missing courses, at-risk grades, and articulation gaps — every finding is linked to an official source URL.",
  },
  {
    q: "Which programs are supported?",
    a: "Five programs today: UF Dietetics BS, UF Warrington Business (BABA), UF Pre-Health advising track, UCF Business (BSBA via DirectConnect), and UCF Pre-Health advising track. More are added as we verify their tracking sheets against official catalogs.",
  },
  {
    q: "Is my transcript stored securely?",
    a: "Yes. Transcripts are uploaded to Emergent Object Storage and referenced only by an internal file_id — never exposed as a raw URL. Only you and your assigned adviser (paid tiers) can download the PDF; downloads are proxied through an authenticated backend endpoint.",
  },
  {
    q: "How accurate is the analysis?",
    a: "Every prerequisite, GPA threshold, and articulation claim traces back to a verification_records row in our database that cites an official URL (catalog, tracking sheet, or Florida SCNS page). We do not invent course codes or credit counts.",
  },
  {
    q: "What if my PDF is a scanned image, not text-selectable?",
    a: "The tool automatically falls back to OCR (Tesseract). If OCR confidence is low, you can enter the courses manually — the same rules and source-verification apply.",
  },
  {
    q: "Does running the diagnostic cost anything?",
    a: "The first Mishap Corrector run is free (per email address). Paid tiers get unlimited runs and additional adviser review on the results.",
  },
  {
    q: "Can I share the results?",
    a: "Yes — every analysis has a persistent /mishap-corrector/results/{analysis_id} URL. That URL is marked noindex,nofollow so it doesn't appear in search results, but you can share it with your adviser or family directly.",
  },
];

export const AUDIT_FAQ = [
  {
    q: "What's included in the Transfer Audit?",
    a: "A comprehensive transcript review against the Common Prerequisites Manual for your major, target-school analysis (UF, UCF, FSU, USF, FIU, or nationwide), identification of completed and missing prerequisites, excess-credit risk assessment, a semester-by-semester roadmap, and a 60-minute strategy call.",
  },
  {
    q: "How much does the Transfer Audit cost?",
    a: "$249 one-time. Not a subscription. Referral codes take $50 off (bringing it to $199).",
  },
  {
    q: "Who is the Transfer Audit for?",
    a: "Florida community-college students from first semester through the semester before transfer, plus out-of-state students considering Florida pathways.",
  },
  {
    q: "How long until I get my report?",
    a: "The written report is delivered within 5 business days of the intake call. The 60-minute strategy call is scheduled after the report is delivered.",
  },
  {
    q: "Is this application help or essay editing?",
    a: "No. The Transfer Audit is admissions strategy. If you want application or essay support, that lives in our Florida Transfer and Selective & Elite tiers.",
  },
  {
    q: "Is it a guarantee of admission?",
    a: "No. We're a consulting service, not the admissions office. We give you the clearest, most-sourced view of your position and what to do next.",
  },
];

export const ABOUT_FAQ = [
  {
    q: "Who founded Beyond Valencia?",
    a: "Taimur Ahmed — a former Valencia College transfer student who went on to earn his ALM from Harvard University in 2017. Beyond Valencia is his response to how little honest, sourced admissions guidance exists for Florida community-college students.",
  },
  {
    q: "Where is Beyond Valencia based?",
    a: "Orlando, Florida. The service focuses on Florida community college → UF, UCF, FSU, USF, FIU, and America's most selective universities.",
  },
  {
    q: "How is Beyond Valencia different from firms like IvyWise or Command Education?",
    a: "Unlike traditional admissions firms that focus on high school seniors applying as freshmen, Beyond Valencia is built specifically for the community college transfer pathway. Every recommendation is grounded in Florida SCNS articulation, the Common Prerequisites Manual, and official university tracking sheets — with source URLs attached to every claim.",
  },
  {
    q: "Do you work with students outside Florida?",
    a: "Yes, for out-of-state students considering Florida transfer pathways, or for elite transfer targets (Harvard, Yale, Columbia, and other selective schools) from any community college.",
  },
];
